import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, IndianRupee, Check, X, Phone, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { TourCard } from '../components/TourCard';
import { allTours } from '../mockData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';

export const TourDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tour = allTours.find(t => t.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!tour) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Tour Not Found</h1>
        <Button onClick={() => navigate('/tours')}>Back to Tours</Button>
      </div>
    );
  }

  // Get related tours from same category
  const relatedTours = allTours
    .filter(t => t.category === tour.category && t.id !== tour.id)
    .slice(0, 3);

  return (
    <div className="tour-details-page">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-end">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${tour.image})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 pb-12 text-white">
          <div className="max-w-4xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full mb-4 text-sm">
              {tour.category.charAt(0).toUpperCase() + tour.category.slice(1)} Tour
            </div>
            <h1 className="text-5xl font-bold mb-4">{tour.title}</h1>
            <div className="flex flex-wrap gap-6 text-lg">
              <div className="flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                {tour.destination}
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                {tour.duration}
              </div>
              <div className="flex items-center font-bold text-2xl">
                <IndianRupee className="w-6 h-6 mr-1" />
                {tour.price.replace('₹', '')} per person
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Tour Details */}
            <div className="lg:col-span-2">
              {/* Photo Gallery */}
              {tour.gallery && tour.gallery.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Photo Gallery</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {tour.gallery.map((photo, index) => (
                      <div key={index} className="relative h-48 rounded-lg overflow-hidden group cursor-pointer">
                        <img
                          src={photo}
                          alt={`${tour.title} - Photo ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <Tabs defaultValue="overview" className="bg-white rounded-lg shadow-lg">
                <TabsList className="w-full justify-start border-b rounded-none h-auto p-0">
                  <TabsTrigger value="overview" className="px-6 py-4 data-[state=active]:border-b-2 data-[state=active]:border-sky-600 rounded-none">Overview</TabsTrigger>
                  <TabsTrigger value="itinerary" className="px-6 py-4 data-[state=active]:border-b-2 data-[state=active]:border-sky-600 rounded-none">Itinerary</TabsTrigger>
                  <TabsTrigger value="inclusions" className="px-6 py-4 data-[state=active]:border-b-2 data-[state=active]:border-sky-600 rounded-none">Inclusions</TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="p-6">
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-4">About This Tour</h2>
                      <p className="text-gray-700 text-lg leading-relaxed">{tour.description}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-3">Tour Highlights</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {tour.highlights.map((highlight, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <span className="text-gray-700">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="itinerary" className="p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Day by Day Itinerary</h2>
                  {tour.itinerary ? (
                    <div className="space-y-6">
                      {tour.itinerary.map((day, index) => (
                        <div key={index} className="border-l-4 border-sky-500 pl-6 pb-6 relative">
                          <div className="absolute -left-3 top-0 w-6 h-6 bg-sky-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {day.day}
                          </div>
                          <h3 className="text-lg font-bold text-gray-800 mb-2">
                            Day {day.day}: {day.title}
                          </h3>
                          <p className="text-gray-700 mb-3">{day.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1 text-sky-600" />
                              Meals: {day.meals}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1 text-emerald-600" />
                              {day.accommodation}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">Detailed itinerary will be shared upon booking.</p>
                  )}
                </TabsContent>
                
                <TabsContent value="inclusions" className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <Check className="w-6 h-6 text-emerald-600 mr-2" />
                        Inclusions
                      </h3>
                      {tour.inclusions ? (
                        <ul className="space-y-2">
                          {tour.inclusions.map((item, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600">Details will be provided upon inquiry.</p>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <X className="w-6 h-6 text-red-600 mr-2" />
                        Exclusions
                      </h3>
                      {tour.exclusions ? (
                        <ul className="space-y-2">
                          {tour.exclusions.map((item, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600">Details will be provided upon inquiry.</p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24 shadow-xl">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-sky-600 mb-2">
                      {tour.price}
                    </div>
                    <p className="text-gray-600">per person</p>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-semibold">{tour.duration}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-gray-600">Destination</span>
                      <span className="font-semibold text-right text-sm">{tour.destination}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Link to="/contact" state={{ selectedTour: tour.title }}>
                      <Button className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white py-6 text-lg">
                        Book This Tour
                      </Button>
                    </Link>
                    
                    <a href="tel:+919833280079">
                      <Button variant="outline" className="w-full border-2 border-sky-600 text-sky-600 hover:bg-sky-50 py-6">
                        <Phone className="w-5 h-5 mr-2" />
                        Call to Inquire
                      </Button>
                    </a>
                  </div>
                  
                  <div className="mt-6 p-4 bg-sky-50 rounded-lg">
                    <p className="text-sm text-gray-700 text-center">
                      Need customization? Our travel experts can tailor this package to your preferences.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tours */}
      {relatedTours.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">You May Also Like</h2>
              <p className="text-gray-600">Explore similar tour packages</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedTours.map((relatedTour) => (
                <TourCard key={relatedTour.id} tour={relatedTour} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};